import { GeneralException } from '@injectivelabs/exceptions';
import { Network, getNetworkInfo, getNetworkEndpoints, } from '@injectivelabs/networks';
import { getStdFee, toBigNumber, getDefaultStdFee, DEFAULT_BLOCK_TIMEOUT_HEIGHT, } from '@injectivelabs/utils';
import { createTransaction } from '../tx.js';
import { TxGrpcApi } from '../api/TxGrpcApi.js';
import { ofacWallets } from '../../../json/index.js';
import { PrivateKey } from '../../accounts/index.js';
import { IndexerGrpcWeb3GwApi } from '../../../client/index.js';
import { getGasPriceBasedOnMessage } from '../../../utils/msgs.js';
import { ChainGrpcAuthApi, ChainGrpcTendermintApi, } from '../../../client/chain/grpc/index.js';
import { ChainRestAuthApi, ChainRestTendermintApi, } from '../../../client/chain/rest/index.js';
/**
 * This class is used to broadcast transactions
 * using a privateKey as a signer
 * for the transactions and broadcasting
 * the transactions directly to the node
 *
 * Mainly used for working in a Node Environment
 */
export class MsgBroadcasterWithPk {
    endpoints;
    chainId;
    evmChainId;
    privateKey;
    simulateTx = false;
    txTimeoutOnFeeDelegation = false;
    useRest = false;
    gasBufferCoefficient = 1.1;
    txTimeout = DEFAULT_BLOCK_TIMEOUT_HEIGHT;
    constructor(options) {
        const network = options.network || Network.MainnetSentry;
        const networkInfo = getNetworkInfo(network);
        const endpoints = getNetworkEndpoints(network);
        this.useRest = options.useRest || false;
        this.gasBufferCoefficient = options.gasBufferCoefficient || 1.1;
        this.simulateTx = options.simulateTx || false;
        this.chainId = options.chainId || networkInfo.chainId;
        this.txTimeout = options.txTimeout || DEFAULT_BLOCK_TIMEOUT_HEIGHT;
        this.evmChainId = options.evmChainId || networkInfo.evmChainId;
        this.endpoints = { ...endpoints, ...(options.endpoints || {}) };
        this.privateKey =
            options.privateKey instanceof PrivateKey
                ? options.privateKey
                : PrivateKey.fromHex(options.privateKey);
        this.txTimeoutOnFeeDelegation =
            options.txTimeoutOnFeeDelegation || this.txTimeoutOnFeeDelegation;
    }
    /**
     * Broadcasting the transaction using the client
     *
     * @param tx
     * @returns {string} transaction hash
     */
    async broadcast(transaction, accountDetails) {
        const { privateKey } = this;
        if (ofacWallets.includes(privateKey.toHex())) {
            throw new GeneralException(new Error('You cannot execute this transaction'));
        }
        const { txRaw } = await this.prepareTxForBroadcast(transaction, accountDetails);
        return await this.broadcastTxRaw(txRaw);
    }
    /**
     * Broadcasting the transaction with fee delegation services
     *
     * @param tx
     * @returns {string} transaction hash
     */
    async broadcastWithFeeDelegation(transaction) {
        const { endpoints, txTimeout, simulateTx, privateKey, evmChainId, txTimeoutOnFeeDelegation, } = this;
        const ethereumWallet = this.privateKey.toHex();
        if (ofacWallets.includes(ethereumWallet)) {
            throw new GeneralException(new Error('You cannot execute this transaction'));
        }
        const msgs = Array.isArray(transaction.msgs)
            ? transaction.msgs
            : [transaction.msgs];
        const tx = {
            ...transaction,
            msgs: msgs,
            ethereumAddress: ethereumWallet,
        };
        const web3Msgs = msgs.map((msg) => msg.toWeb3());
        if (!evmChainId) {
            throw new GeneralException(new Error('Please provide ethereumChainId'));
        }
        let timeoutHeight = undefined;
        if (txTimeoutOnFeeDelegation) {
            const latestBlock = await new ChainGrpcTendermintApi(endpoints.grpc).fetchLatestBlock();
            const latestHeight = latestBlock.header.height;
            timeoutHeight = toBigNumber(latestHeight).plus(txTimeout).toNumber();
        }
        const transactionApi = new IndexerGrpcWeb3GwApi(endpoints.web3gw || endpoints.indexer);
        const txResponse = await transactionApi.prepareTxRequest({
            memo: tx.memo,
            message: web3Msgs,
            address: tx.ethereumAddress,
            chainId: evmChainId,
            gasLimit: getGasPriceBasedOnMessage(msgs),
            estimateGas: simulateTx || false,
            timeoutHeight,
        });
        const signature = await privateKey.signTypedData(JSON.parse(txResponse.data));
        const response = await transactionApi.broadcastTxRequest({
            txResponse,
            message: web3Msgs,
            chainId: evmChainId,
            signature: `0x${Buffer.from(signature).toString('hex')}`,
        });
        return await new TxGrpcApi(endpoints.grpc).fetchTxPoll(response.txHash);
    }
    /**
     * Broadcasting the transaction using the client
     *
     * @param tx
     * @returns {string} transaction hash
     */
    async simulate(transaction, accountDetails) {
        const { privateKey, endpoints, chainId } = this;
        const ethereumWallet = this.privateKey.toHex();
        if (ofacWallets.includes(ethereumWallet)) {
            throw new GeneralException(new Error('You cannot execute this transaction'));
        }
        const tx = {
            ...transaction,
            msgs: Array.isArray(transaction.msgs)
                ? transaction.msgs
                : [transaction.msgs],
        };
        /** Account Details * */
        const publicKey = privateKey.toPublicKey();
        const actualAccountDetails = await this.getAccountDetails(accountDetails);
        /** Block Details */
        const timeoutHeight = await this.getTimeoutHeight();
        /** Prepare the Transaction * */
        const { txRaw } = createTransaction({
            memo: tx.memo || '',
            fee: getDefaultStdFee(),
            message: tx.msgs,
            timeoutHeight: timeoutHeight.toNumber(),
            pubKey: publicKey.toBase64(),
            sequence: actualAccountDetails.sequence,
            accountNumber: actualAccountDetails.accountNumber,
            chainId: chainId,
        });
        /** Append Blank Signatures */
        txRaw.signatures = [new Uint8Array(0)];
        /** Simulate transaction */
        const simulationResponse = await new TxGrpcApi(endpoints.grpc).simulate(txRaw);
        return simulationResponse;
    }
    /**
     * In case we don't want to simulate the transaction
     * we get the gas limit based on the message type.
     *
     * If we want to simulate the transaction we set the
     * gas limit based on the simulation and add a small multiplier
     * to be safe (factor of 1.1 (or user specified))
     */
    async getTxWithStdFee(args) {
        const { simulateTx, gasBufferCoefficient } = this;
        if (!simulateTx) {
            return createTransaction(args);
        }
        const result = await this.simulateTxRaw(args);
        if (!result.gasInfo?.gasUsed) {
            return createTransaction(args);
        }
        const stdGasFee = getStdFee({
            ...args.fee,
            gas: toBigNumber(result.gasInfo.gasUsed)
                .times(gasBufferCoefficient)
                .toFixed(),
        });
        return createTransaction({ ...args, fee: stdGasFee });
    }
    /**
     * Create TxRaw and simulate it
     */
    async simulateTxRaw(args) {
        const { endpoints } = this;
        const { txRaw } = createTransaction(args);
        txRaw.signatures = [new Uint8Array(0)];
        const simulationResponse = await new TxGrpcApi(endpoints.grpc).simulate(txRaw);
        return simulationResponse;
    }
    async prepareTxForBroadcast(transaction, accountDetails) {
        const { chainId, privateKey } = this;
        const msgs = Array.isArray(transaction.msgs)
            ? transaction.msgs
            : [transaction.msgs];
        const tx = {
            ...transaction,
            msgs: msgs,
        };
        /** Account Details * */
        const publicKey = privateKey.toPublicKey();
        const actualAccountDetails = await this.getAccountDetails(accountDetails);
        /** Block Details */
        const timeoutHeight = await this.getTimeoutHeight();
        const gas = (transaction.gas?.gas || getGasPriceBasedOnMessage(msgs)).toString();
        /** Prepare the Transaction * */
        const { signBytes, txRaw } = await this.getTxWithStdFee({
            memo: tx.memo || '',
            message: msgs,
            fee: getStdFee({ ...tx.gas, gas }),
            timeoutHeight: timeoutHeight.toNumber(),
            pubKey: publicKey.toBase64(),
            sequence: actualAccountDetails.sequence,
            accountNumber: actualAccountDetails.accountNumber,
            chainId: chainId,
        });
        /** Sign transaction */
        const signature = await privateKey.sign(Buffer.from(signBytes));
        /** Append Signatures */
        txRaw.signatures = [signature];
        return { txRaw, accountDetails: actualAccountDetails };
    }
    async getAccountDetails(accountDetails) {
        const { useRest } = this;
        if (accountDetails) {
            return accountDetails;
        }
        if (useRest) {
            const { privateKey, endpoints } = this;
            const accountDetailsResponse = await new ChainRestAuthApi(endpoints.grpc).fetchAccount(privateKey.toBech32());
            const baseAccount = accountDetailsResponse.account.base_account;
            return {
                accountNumber: parseInt(baseAccount.account_number),
                sequence: parseInt(baseAccount.sequence),
                address: baseAccount.address,
                pubKey: baseAccount.pub_key
                    ? {
                        key: baseAccount.pub_key.key,
                        type: baseAccount.pub_key['@type'],
                    }
                    : {
                        key: '',
                        type: '',
                    },
            };
        }
        const { privateKey, endpoints } = this;
        const accountDetailsResponse = await new ChainGrpcAuthApi(endpoints.grpc).fetchAccount(privateKey.toBech32());
        const baseAccount = accountDetailsResponse.baseAccount;
        return {
            accountNumber: baseAccount.accountNumber,
            sequence: baseAccount.sequence,
            address: baseAccount.address,
            pubKey: baseAccount.pubKey
                ? {
                    key: baseAccount.pubKey.key,
                    type: baseAccount.pubKey.typeUrl,
                }
                : {
                    key: '',
                    type: '',
                },
        };
    }
    async getTimeoutHeight() {
        const { useRest, endpoints, txTimeout } = this;
        if (useRest) {
            const latestBlock = await new ChainRestTendermintApi(endpoints.grpc).fetchLatestBlock();
            const latestHeight = latestBlock.header.height;
            return toBigNumber(latestHeight).plus(txTimeout);
        }
        const latestBlock = await new ChainGrpcTendermintApi(endpoints.grpc).fetchLatestBlock();
        const latestHeight = latestBlock.header.height;
        return toBigNumber(latestHeight).plus(txTimeout);
    }
    async broadcastTxRaw(txRaw) {
        const { endpoints, txTimeout } = this;
        const txResponse = await new TxGrpcApi(endpoints.grpc).broadcast(txRaw, {
            txTimeout,
        });
        if (txResponse.code !== 0) {
            throw new GeneralException(new Error(`Transaction failed to be broadcasted - ${txResponse.rawLog} - ${txResponse.txHash}`));
        }
        return txResponse;
    }
}
